import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import validate from "validate.js";
import { showSnackbar } from "../../app/slices/commonSlice";
import {
  addNews,
  resetStateNews,
  updateNews,
} from "../../app/slices/newsSlice";
import { getBase64, getObjectDifference } from "../../app/utils";
import FileInput from "../../components/fileInput/fileInput";

const ManageNewsDialog = ({
  open,
  action = "POST",
  selectedItem,
  handleClose,
}) => {
  const {
    status: statusPost,
    data: dataPost,
    error: errorPost,
  } = useSelector(({ news }) => news.post);

  const {
    status: statusPut,
    data: dataPut,
    error: errorPut,
  } = useSelector(({ news }) => news.put);

  const dispatch = useDispatch();

  const validateSchema = {
    titleEN: { presence: { allowEmpty: false } },
    titleSW: { presence: { allowEmpty: false } },
    descriptionEN: { presence: { allowEmpty: false } },
    descriptionSW: { presence: { allowEmpty: false } },
  };

  const [errors, setErrors] = useState({});
  const [formValues, setFormValues] = useState(() => {
    if (selectedItem) {
      return {
        titleEN: selectedItem.TITLE_EN || "",
        titleSW: selectedItem.TITLE_SW || "",
        descriptionEN: selectedItem.DESCRIPTION_EN || "",
        descriptionSW: selectedItem.DESCRIPTION_SW || "",
        status: selectedItem.STATUS || "A",
        coverImage: selectedItem.COVER_IMAGE || null,
      };
    }
    return {};
  });

  const statuses = [
    { value: "A", label: "Active" },
    { value: "I", label: "Inactive" },
    { value: "D", label: "Deleted" },
  ];

  const hasChanges = useMemo(() => {
    if (action !== "UPDATE" || !selectedItem) return true;
    const initialPayload = {
      TITLE_EN: selectedItem.TITLE_EN || "",
      TITLE_SW: selectedItem.TITLE_SW || "",
      DESCRIPTION_EN: selectedItem.DESCRIPTION_EN || "",
      DESCRIPTION_SW: selectedItem.DESCRIPTION_SW || "",
      STATUS: selectedItem.STATUS || "A",
    };
    const currentPayload = {
      TITLE_EN: formValues.titleEN,
      TITLE_SW: formValues.titleSW,
      DESCRIPTION_EN: formValues.descriptionEN,
      DESCRIPTION_SW: formValues.descriptionSW,
      STATUS: formValues.status,
    };
    return Object.keys(getObjectDifference(initialPayload, currentPayload)).length > 0;
  }, [formValues, selectedItem, action]);

  useEffect(() => {
    dispatch(resetStateNews(["post", "put"]));
    return () => {
      dispatch(resetStateNews(["post", "put"]));
    };
  }, [dispatch]);

  useEffect(() => {
    if (dataPut || dataPost) {
      dispatch(
        showSnackbar({
          open: true,
          type: "success",
          message: `News ${dataPut ? "updated" : "posted"} successfully.`,
        }),
      );
      dispatch(resetStateNews(["post", "put"]));
      handleClose();
    }
  }, [dataPut, dataPost, dispatch, handleClose]);

  useEffect(() => {
    if (errorPut || errorPost) {
      dispatch(
        showSnackbar({
          open: true,
          type: "error",
          message:
            errorPut?.message || errorPost?.message || "An error occurred",
        }),
      );
    }
  }, [errorPut, errorPost, dispatch]);

  const handleChange = (e) => {
    if (action === "VIEW") {
      dispatch(
        showSnackbar({
          open: true,
          message: "Cannot edit in view mode",
          type: "warning",
        }),
      );
      return;
    }

    const { name, value, files } = e.target;

    if (files?.length) {
      if (files[0]?.size > 5000000) {
        dispatch(
          showSnackbar({
            open: true,
            type: "error",
            message: "Upload should not exceed 5MB",
          }),
        );
        return;
      }

      setFormValues((prev) => ({
        ...prev,
        document: files[0],
      }));
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    const validationErrors = validate(formValues, validateSchema);

    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    const payload = {
      TITLE_EN: formValues.titleEN,
      TITLE_SW: formValues.titleSW,
      DESCRIPTION_EN: formValues.descriptionEN,
      DESCRIPTION_SW: formValues.descriptionSW,
    };

    if (action === "UPDATE") {
      payload.STATUS = formValues.status;
    }

    if (action === "POST") {
      if (!formValues.document) {
        dispatch(
          showSnackbar({
            open: true,
            message: "Cover Image cannot be empty",
            type: "error",
          }),
        );
        return;
      }

      payload.COVER_IMAGE = await getBase64(formValues.document);
    }

    if (action === "POST") {
      dispatch(addNews(payload));
    } else if (action === "UPDATE") {
      if (!hasChanges) {
        dispatch(
          showSnackbar({
            open: true,
            message: "No changes to submit",
            type: "info",
          }),
        );
        return;
      }
      
      const initialPayload = {
        TITLE_EN: selectedItem.TITLE_EN || "",
        TITLE_SW: selectedItem.TITLE_SW || "",
        DESCRIPTION_EN: selectedItem.DESCRIPTION_EN || "",
        DESCRIPTION_SW: selectedItem.DESCRIPTION_SW || "",
        STATUS: selectedItem.STATUS || "A",
      };
      
      const diff = getObjectDifference(initialPayload, payload);
      dispatch(updateNews({ id: selectedItem.NEWS_ID, payload: diff }));
    }
  };

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={handleClose}>
      <DialogTitle sx={{ fontWeight: "bold" }}>{action} NEWS</DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={12} sx={{ textAlign: "center" }}>
              {formValues.coverImage && (
                <Box
                  component="img"
                  src={formValues.coverImage}
                  sx={{
                    maxHeight: 200,
                    objectFit: "cover",
                  }}
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Title English"
                name="titleEN"
                value={formValues.titleEN || ""}
                onChange={handleChange}
                error={Boolean(errors.titleEN)}
                helperText={errors.titleEN?.[0] || ""}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Title Swahili"
                name="titleSW"
                value={formValues.titleSW || ""}
                onChange={handleChange}
                error={Boolean(errors.titleSW)}
                helperText={errors.titleSW?.[0] || ""}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description English"
                name="descriptionEN"
                value={formValues.descriptionEN || ""}
                onChange={handleChange}
                error={Boolean(errors.descriptionEN)}
                helperText={errors.descriptionEN?.[0] || ""}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description Swahili"
                name="descriptionSW"
                value={formValues.descriptionSW || ""}
                onChange={handleChange}
                error={Boolean(errors.descriptionSW)}
                helperText={errors.descriptionSW?.[0] || ""}
              />
            </Grid>

            {selectedItem && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  name="status"
                  value={formValues.status || ""}
                  onChange={handleChange}
                >
                  {statuses.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {action === "POST" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FileInput
                  onChange={handleChange}
                  file={formValues.document}
                  fileType="image/*"
                  label="Cover Image"
                  name="document"
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        {action !== "VIEW" && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={statusPut === "loading" || statusPost === "loading" || !hasChanges}
          >
            {(statusPut === "loading" || statusPost === "loading") && (
              <CircularProgress size={18} sx={{ mr: 1 }} />
            )}
            {action === "UPDATE" ? "Update" : "Post"}
          </Button>
        )}

        <Button variant="outlined" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageNewsDialog;
